import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  DashboardCard,
  EmptyState,
  PageHeader,
  SectionHeader,
  Skeleton,
} from '@zafroi/ui-kit';
import {
  AlertCircle,
  Check,
  Copy,
  KeyRound,
  Power,
  RefreshCw,
  ShieldAlert,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listApiClients,
  type PlatformApiClient,
} from '@/services/platform/apiClients';
import {
  listApiKeys,
  revokeApiKey,
  rotateApiKey,
  type PlatformApiKey,
  type PlatformApiKeyWithSecret,
} from '@/services/platform/apiKeys';

interface ClientRow {
  client: PlatformApiClient;
  keys: PlatformApiKey[];
  loadingKeys: boolean;
}

export function ApiKeysPage() {
  const [rows, setRows] = useState<ClientRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<PlatformApiKeyWithSecret | null>(null);
  const [busyKeyId, setBusyKeyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const clients = await listApiClients();
      const initialRows: ClientRow[] = clients.map((c) => ({
        client: c,
        keys: [],
        loadingKeys: true,
      }));
      setRows(initialRows);
      // Fan out key fetches in parallel; each row updates as it lands.
      await Promise.all(
        clients.map(async (c, idx) => {
          try {
            const keys = await listApiKeys(c.id);
            setRows((prev) => {
              if (!prev) return prev;
              const next = [...prev];
              next[idx] = { ...next[idx], keys, loadingKeys: false };
              return next;
            });
          } catch {
            setRows((prev) => {
              if (!prev) return prev;
              const next = [...prev];
              next[idx] = { ...next[idx], loadingKeys: false };
              return next;
            });
          }
        }),
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load API clients';
      setError(msg);
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleRotate(key: PlatformApiKey) {
    if (busyKeyId) return;
    const ok = window.confirm(
      `Rotate "${key.name}"? The old key will stop working immediately. Any integration using it must be updated with the new secret.`,
    );
    if (!ok) return;
    setBusyKeyId(key.id);
    try {
      const rotated = await rotateApiKey(key.id, 'Rotated from Engage developer console');
      setRevealed(rotated);
      toast.success('Key rotated — copy the new secret now');
      void load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Rotation failed';
      toast.error(msg);
    } finally {
      setBusyKeyId(null);
    }
  }

  async function handleRevoke(key: PlatformApiKey) {
    if (busyKeyId) return;
    const ok = window.confirm(
      `Revoke "${key.name}"? Any integration using this key will stop working immediately. This cannot be undone — rotate instead if you want to keep the integration alive.`,
    );
    if (!ok) return;
    setBusyKeyId(key.id);
    try {
      await revokeApiKey(key.id, 'Revoked from Engage developer console');
      toast.success('Key revoked');
      void load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Revoke failed';
      toast.error(msg);
    } finally {
      setBusyKeyId(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="API keys"
        description="Manage the keys behind your integrations. Rotate when a secret may have leaked; revoke when retiring an integration."
        actions={
          <Button variant="outline" size="sm" onClick={() => void load()}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Refresh
          </Button>
        }
      />

      {revealed && <NewSecretBanner secret={revealed} onClose={() => setRevealed(null)} />}

      <SectionHeader
        title="Your API clients"
        description="Each client groups the keys for one integration (a website, a backend service, a marketplace connector). Issue new clients from Operations → Developer → Integrations."
        withDivider
      />

      {error && (
        <DashboardCard icon={ShieldAlert} title="Couldn't load API clients">
          <p className="text-[13px] text-muted-foreground">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => void load()}>
            Try again
          </Button>
        </DashboardCard>
      )}

      {!rows && !error && (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      )}

      {rows && rows.length === 0 && !error && (
        <EmptyState
          icon={KeyRound}
          title="No API clients yet"
          description="API clients are the parent records that hold your keys. Create one from Operations → Developer → Integrations, then come back here to manage its keys."
        />
      )}

      {rows && rows.length > 0 && (
        <div className="space-y-4">
          {rows.map((row) => (
            <ClientCard
              key={row.client.id}
              row={row}
              busyKeyId={busyKeyId}
              onRotate={handleRotate}
              onRevoke={handleRevoke}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── ClientCard ─────────────────────────────────

function ClientCard({
  row,
  busyKeyId,
  onRotate,
  onRevoke,
}: {
  row: ClientRow;
  busyKeyId: string | null;
  onRotate: (k: PlatformApiKey) => void;
  onRevoke: (k: PlatformApiKey) => void;
}) {
  const { client, keys, loadingKeys } = row;
  const activeKeys = useMemo(() => keys.filter((k) => k.status === 'active'), [keys]);
  const tier = client.tier ?? 'INTEGRATION';

  return (
    <DashboardCard>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[14px] font-semibold text-foreground">{client.name}</h3>
            <TierBadge tier={tier} />
            {client.status === 'inactive' && (
              <Badge tone="neutral" className="text-[10px] uppercase tracking-wide">
                Inactive
              </Badge>
            )}
          </div>
          {client.description && (
            <p className="text-[12.5px] text-muted-foreground">{client.description}</p>
          )}
          {client.systemRef && (
            <p className="text-[11.5px] text-muted-foreground">
              <span className="font-medium text-foreground">System:</span> {client.systemRef}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 border-t border-border/60 pt-3">
        {loadingKeys && <Skeleton className="h-12" />}
        {!loadingKeys && keys.length === 0 && (
          <p className="text-[12.5px] text-muted-foreground">
            No keys issued for this client yet. Mint one from Operations → Developer → Integrations.
          </p>
        )}
        {!loadingKeys && keys.length > 0 && (
          <ul className="divide-y divide-border/60">
            {keys.map((k) => (
              <li
                key={k.id}
                className="flex flex-wrap items-center justify-between gap-2 py-2"
              >
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-medium text-foreground">{k.name}</span>
                    <KeyStatusBadge status={k.status} />
                  </div>
                  <code className="font-mono text-[11.5px] text-muted-foreground">
                    {k.maskedKey}
                  </code>
                  <p className="text-[11px] text-muted-foreground">
                    {k.lastUsedAt
                      ? `Last used ${formatRelativeTime(k.lastUsedAt)}`
                      : 'Never used'}
                    {' · '}
                    {`Issued ${formatRelativeTime(k.createdAt)}`}
                  </p>
                </div>
                {k.status === 'active' && (
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busyKeyId === k.id}
                      onClick={() => onRotate(k)}
                    >
                      <RefreshCw className="mr-1.5 h-3 w-3" />
                      Rotate
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busyKeyId === k.id}
                      onClick={() => onRevoke(k)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Power className="mr-1.5 h-3 w-3" />
                      Revoke
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {!loadingKeys && activeKeys.length === 0 && keys.length > 0 && (
          <div className="mt-2 flex items-start gap-1.5 rounded-md border border-warning/30 bg-warning/5 px-2.5 py-1.5 text-[11.5px] text-warning">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              This client has no active keys — any integration using it is currently broken. Issue
              a new key from Operations.
            </span>
          </div>
        )}
      </div>
    </DashboardCard>
  );
}

// ───────────────────────── NewSecretBanner ──────────────────────────────

function NewSecretBanner({
  secret,
  onClose,
}: {
  secret: PlatformApiKeyWithSecret;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(secret.plaintextKey).then(
      () => {
        setCopied(true);
        toast.success('New key copied to clipboard');
        setTimeout(() => setCopied(false), 1500);
      },
      () => toast.error('Copy failed — select the text manually'),
    );
  }
  return (
    <div className="rounded-lg border border-primary/40 bg-primary/5 p-4">
      <div className="flex items-start gap-3">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-[13px] font-semibold text-foreground">
              New key issued — copy it now
            </h4>
            <Badge tone="accent" className="text-[10px] uppercase tracking-wide">
              Visible once
            </Badge>
          </div>
          <p className="text-[12px] text-muted-foreground">
            This is the only time we'll show the full secret. After you close this banner, only the
            prefix is recoverable. Update your integration with the value below before doing
            anything else.
          </p>
          <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 font-mono text-[12.5px]">
            <code className="flex-1 break-all">{secret.plaintextKey}</code>
            <Button size="sm" variant="outline" onClick={copy}>
              {copied ? (
                <>
                  <Check className="mr-1 h-3 w-3" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="mr-1 h-3 w-3" />
                  Copy
                </>
              )}
            </Button>
          </div>
          <div className="pt-1">
            <Button size="sm" variant="ghost" onClick={onClose}>
              I've saved it — close this
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── helpers ────────────────────────────────────

function TierBadge({
  tier,
}: {
  tier: 'PLATFORM_ADMIN' | 'TENANT_ADMIN' | 'INTEGRATION' | 'PORTAL';
}) {
  const map: Record<typeof tier, { label: string; tone: 'accent' | 'info' | 'primary' | 'neutral' }> =
    {
      PORTAL: { label: 'Portal', tone: 'accent' },
      INTEGRATION: { label: 'Integration', tone: 'info' },
      TENANT_ADMIN: { label: 'Tenant admin', tone: 'primary' },
      PLATFORM_ADMIN: { label: 'Platform admin', tone: 'neutral' },
    };
  const v = map[tier];
  return (
    <Badge tone={v.tone} className="text-[10px] uppercase tracking-wide">
      {v.label}
    </Badge>
  );
}

function KeyStatusBadge({ status }: { status: 'active' | 'revoked' | 'expired' }) {
  if (status === 'active') {
    return (
      <Badge tone="success" className="text-[10px] uppercase tracking-wide">
        Active
      </Badge>
    );
  }
  if (status === 'revoked') {
    return (
      <Badge tone="destructive" className="text-[10px] uppercase tracking-wide">
        Revoked
      </Badge>
    );
  }
  return (
    <Badge tone="warning" className="text-[10px] uppercase tracking-wide">
      Expired
    </Badge>
  );
}

function formatRelativeTime(iso: string): string {
  const d = new Date(iso);
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  if (Number.isNaN(diffSec)) return 'unknown';
  const abs = Math.abs(diffSec);
  if (abs < 60) return 'just now';
  if (abs < 3600) return `${Math.round(abs / 60)} min ago`;
  if (abs < 86_400) return `${Math.round(abs / 3600)} h ago`;
  if (abs < 86_400 * 30) return `${Math.round(abs / 86_400)} d ago`;
  return d.toISOString().slice(0, 10);
}
